/**
 * Entities: first-class objects with identity, a name, a place in the
 * hierarchy and a set of data-only components (architecture §3.1).
 *
 * Records are immutable. Every change produces a new record, so a record's
 * identity only changes when the entity changes (review F-3), which is what
 * React snapshots and undo memos rely on.
 */
import type { ComponentData } from "./schema/defineComponent";

/** A stable, level-unique id from the manifest (A-37). Deep links resolve through it. */
export type EntityId = string & { readonly __brand: "EntityId" };

export function entityId(id: string): EntityId {
  return id as EntityId;
}

/** An EntityRecord as JSON (components as an object), for command memos. */
export interface PlainEntity {
  readonly id: string;
  readonly name: string;
  readonly className: string | null;
  readonly parent: string | null;
  readonly children: readonly string[];
  readonly visible: boolean;
  readonly components: Readonly<Record<string, ComponentData>>;
  readonly disabled?: readonly string[];
}

/**
 * A component's data while it's turned on: undefined when the entity doesn't
 * have it or has it turned off (Unity's enabled checkbox), which acts as absent.
 */
export function enabledComponent(entity: EntityRecord, type: string): ComponentData | undefined {
  return entity.disabled?.has(type) ? undefined : entity.components.get(type);
}

export function toPlainEntity(record: EntityRecord): PlainEntity {
  const { disabled, ...rest } = record;
  return {
    ...rest,
    components: Object.fromEntries(record.components),
    ...(disabled && disabled.size > 0 && { disabled: [...disabled] }),
  };
}

export function fromPlainEntity(plain: PlainEntity): EntityRecord {
  const { disabled, ...rest } = plain;
  return {
    ...rest,
    id: entityId(plain.id),
    parent: plain.parent === null ? null : entityId(plain.parent),
    children: plain.children.map(entityId),
    components: new Map(Object.entries(plain.components)),
    ...(disabled && { disabled: new Set(disabled) }),
  };
}

export interface EntityRecord {
  readonly id: EntityId;
  /** Display label; unique within a level so it works in deep links. */
  readonly name: string;
  /** Optional class name shown in the Inspector header (like an actor class), e.g. `RoleZone`. */
  readonly className: string | null;
  readonly parent: EntityId | null;
  /** Ordered children. */
  readonly children: readonly EntityId[];
  readonly visible: boolean;
  /** One component per type (architecture §3.1). */
  readonly components: ReadonlyMap<string, ComponentData>;
  /**
   * Components turned off (Unity's enabled checkbox): their data stays, but
   * they act as if absent. Omitted when none are.
   */
  readonly disabled?: ReadonlySet<string>;
}
